import React from "react";
import L from "leaflet";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "./AccessPointsMap.css";
import { useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";

const getGoogleMapsUrl = (latitude: number, longitude: number) =>
  `https://www.google.com/maps?q=${latitude},${longitude}`;

const formatCoordinate = (coordinate: number) =>
  Number.isInteger(coordinate) ? `${coordinate}` : coordinate.toFixed(6);

const DEFAULT_MAP_ZOOM = 18;
const FOCUS_MAP_ZOOM = 19;
const FIT_BOUNDS_PADDING: L.PointExpression = [80, 80];
const FIT_BOUNDS_MAX_ZOOM = 18;
const MAP_LAYOUT_RETRY_DELAYS_MS = [0, 100, 300, 600, 1000];

const refreshMapLayout = (map: L.Map) => {
  map.invalidateSize({ animate: false });
  const center = map.getCenter();
  const zoom = map.getZoom();
  map.setView(center, zoom, { animate: false });
};

const defaultMarkerIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = defaultMarkerIcon;

const closeAllPopups = (map: L.Map) => {
  map.closePopup();
  map.eachLayer((layer) => {
    if (layer instanceof L.Marker) {
      layer.closePopup();
    }
  });
};

const fitMapToAccessPoints = (
  map: L.Map,
  accessPoints: AccessPoint[],
  animate = true
) => {
  if (accessPoints.length === 0) return;

  closeAllPopups(map);

  if (accessPoints.length === 1) {
    const point = accessPoints[0];
    map.setView([point.latitude, point.longitude], DEFAULT_MAP_ZOOM, {
      animate,
    });
    return;
  }

  const bounds = L.latLngBounds(
    accessPoints.map(
      (point) => [point.latitude, point.longitude] as [number, number]
    )
  );
  map.fitBounds(bounds, {
    padding: FIT_BOUNDS_PADDING,
    maxZoom: FIT_BOUNDS_MAX_ZOOM,
    animate,
  });
};

const MapResizeHandler = () => {
  const map = useMap();

  React.useEffect(() => {
    const container = map.getContainer();
    const invalidate = () => {
      refreshMapLayout(map);
    };

    invalidate();
    const timeoutIds = MAP_LAYOUT_RETRY_DELAYS_MS.map((delay) =>
      window.setTimeout(invalidate, delay)
    );

    const observer = new ResizeObserver(invalidate);
    observer.observe(container);

    window.addEventListener("resize", invalidate);

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      window.removeEventListener("resize", invalidate);
      observer.disconnect();
    };
  }, [map]);

  return null;
};

const FitBoundsHandler = ({
  accessPoints,
  skip,
}: {
  accessPoints: AccessPoint[];
  skip: boolean;
}) => {
  const map = useMap();
  const wasFocusedRef = React.useRef(false);

  React.useEffect(() => {
    if (skip) {
      wasFocusedRef.current = true;
      return;
    }

    if (accessPoints.length === 0) return;

    const animate = wasFocusedRef.current;
    wasFocusedRef.current = false;
    fitMapToAccessPoints(map, accessPoints, animate);
  }, [accessPoints, map, skip]);

  return null;
};

const ResetViewHandler = ({
  accessPoints,
  resetViewKey,
  resetButtonVariant,
  markerRefs,
}: {
  accessPoints: AccessPoint[];
  resetViewKey: number;
  resetButtonVariant: "showAll" | "center";
  markerRefs: React.MutableRefObject<Record<string | number, L.Marker>>;
}) => {
  const map = useMap();

  React.useEffect(() => {
    if (resetViewKey === 0 || accessPoints.length === 0) return;

    if (resetButtonVariant === "center" && accessPoints.length === 1) {
      const point = accessPoints[0];
      closeAllPopups(map);
      refreshMapLayout(map);
      map.setView([point.latitude, point.longitude], FOCUS_MAP_ZOOM, {
        animate: true,
      });

      const timeoutId = window.setTimeout(() => {
        const marker = markerRefs.current[point.id];
        if (marker && map.hasLayer(marker)) {
          try {
            marker.openPopup();
          } catch (error) {
            console.error(error);
          }
        }
      }, 150);

      return () => window.clearTimeout(timeoutId);
    }

    fitMapToAccessPoints(map, accessPoints, true);
  }, [accessPoints, map, markerRefs, resetButtonVariant, resetViewKey]);

  return null;
};

const FocusPointHandler = ({
  accessPoints,
  focusedAccessPointId,
  markerRefs,
}: {
  accessPoints: AccessPoint[];
  focusedAccessPointId: number | null;
  markerRefs: React.MutableRefObject<Record<string | number, L.Marker>>;
}) => {
  const map = useMap();
  const isInitialFocusRef = React.useRef(true);

  React.useEffect(() => {
    if (!focusedAccessPointId) return;

    const point = accessPoints.find((item) => item.id === focusedAccessPointId);
    if (!point) return;

    let cancelled = false;
    const animate = !isInitialFocusRef.current;
    isInitialFocusRef.current = false;

    const closeAllPopupsOnMap = () => closeAllPopups(map);

    const openPopup = () => {
      if (cancelled) return;

      const marker = markerRefs.current[focusedAccessPointId];
      if (!marker || !map.hasLayer(marker)) return;

      try {
        marker.openPopup();
      } catch (error) {
        console.error(error);
      }
    };

    const applyFocus = (withAnimation: boolean) => {
      refreshMapLayout(map);
      closeAllPopupsOnMap();
      map.setView([point.latitude, point.longitude], FOCUS_MAP_ZOOM, {
        animate: withAnimation,
      });
    };

    const onMoveEnd = () => {
      map.off("moveend", onMoveEnd);
      window.setTimeout(openPopup, 100);
    };

    map.on("moveend", onMoveEnd);
    applyFocus(animate);

    const fallbackTimeoutId = window.setTimeout(() => {
      if (cancelled) return;
      map.off("moveend", onMoveEnd);
      openPopup();
    }, 600);

    const layoutRetryIds = MAP_LAYOUT_RETRY_DELAYS_MS.slice(1).map((delay) =>
      window.setTimeout(() => applyFocus(false), delay)
    );

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimeoutId);
      layoutRetryIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      map.off("moveend", onMoveEnd);
      closeAllPopupsOnMap();
    };
  }, [accessPoints, focusedAccessPointId, map, markerRefs]);

  return null;
};

type AccessPoint = {
  id: string | number;
  name: string;
  latitude: number;
  longitude: number;
};

type AccessPointsMapProps = {
  accessPoints: AccessPoint[];
  height?: number | string;
  focusedAccessPointId?: number | null;
  onResetFocus?: () => void;
  resetButtonVariant?: "showAll" | "center";
};

const AccessPointsMap = ({
  accessPoints,
  height = 400,
  focusedAccessPointId = null,
  onResetFocus,
  resetButtonVariant = "showAll",
}: AccessPointsMapProps) => {
  const labels = useAppSelector(selectLabels);
  const [isMapReady, setIsMapReady] = React.useState(false);
  const [resetViewKey, setResetViewKey] = React.useState(0);
  const markerRefs = React.useRef<Record<string | number, L.Marker>>({});
  const firstPoint = accessPoints[0];

  const center: [number, number] = firstPoint
    ? [firstPoint.latitude, firstPoint.longitude]
    : [19.4326, -99.1332];

  React.useEffect(() => {
    setIsMapReady(true);

    return () => {
      setIsMapReady(false);
    };
  }, []);

  const handleResetView = () => {
    onResetFocus?.();
    setResetViewKey((key) => key + 1);
  };

  if (!isMapReady) {
    return <div className="access-points-map" style={{ height }} />;
  }

  return (
    <div className="access-points-map" style={{ height }}>
      <button
        type="button"
        className="access-points-map__reset-button"
        onClick={handleResetView}
      >
        {resetButtonVariant === "center"
          ? labels.center_map
          : labels.show_all_on_map}
      </button>
      <MapContainer
        center={center}
        zoom={DEFAULT_MAP_ZOOM}
        className="access-points-map__container"
      >
        <MapResizeHandler />
        <FitBoundsHandler
          accessPoints={accessPoints}
          skip={focusedAccessPointId != null}
        />
        <FocusPointHandler
          accessPoints={accessPoints}
          focusedAccessPointId={focusedAccessPointId}
          markerRefs={markerRefs}
        />
        <ResetViewHandler
          accessPoints={accessPoints}
          resetViewKey={resetViewKey}
          resetButtonVariant={resetButtonVariant}
          markerRefs={markerRefs}
        />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />

        {accessPoints.map((point) => (
          <Marker
            key={point.id}
            position={[point.latitude, point.longitude]}
            ref={(marker) => {
              if (marker) {
                markerRefs.current[point.id] = marker;
              } else {
                delete markerRefs.current[point.id];
              }
            }}
          >
            <Popup autoPan={false}>
              <div className="access-points-map__popup">
                <strong>{point.name}</strong>
                <div className="access-points-map__popup-coordinates">
                  <span>
                    {labels.latitude}: {formatCoordinate(point.latitude)}
                  </span>
                  <span>
                    {labels.longitude}: {formatCoordinate(point.longitude)}
                  </span>
                </div>
                <a
                  className="access-points-map__popup-link"
                  href={getGoogleMapsUrl(point.latitude, point.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {labels.open_in_google_maps}
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default AccessPointsMap;
